import { type FormEvent, useEffect, useRef, useState } from 'react';
import { can } from '../lib/permissions';
import { isAdmin } from '../lib/roles';
import { PRIORITIES, type Priority } from '../lib/types';
import { useStore } from '../store/useStore';

/**
 * Leaving the assignee alone means "let the field decide", which is a different
 * instruction from "nobody" — so it is its own option rather than the empty one.
 * Safe as a plain word: every real value in this select is a uuid.
 */
const AUTO = 'auto';

/**
 * Creating a task is one deliberate act with a form, not something that happens
 * because a stray keystroke landed in a text box on the board.
 */
export function CreateTaskDialog({
  defaultFieldId,
  onClose,
}: {
  defaultFieldId: string | null;
  onClose: () => void;
}) {
  const fields = useStore((s) => s.fields);
  const members = useStore((s) => s.members);
  const access = useStore((s) => s.access);
  const user = useStore((s) => s.user);
  const createTask = useStore((s) => s.createTask);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [fieldId, setFieldId] = useState(defaultFieldId ?? fields[0]?.id ?? '');
  const [assignee, setAssignee] = useState<string>(AUTO);
  const [priority, setPriority] = useState<Priority>('medium');

  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    dialogRef.current?.showModal();
    titleRef.current?.focus();
  }, []);

  const field = fields.find((f) => f.id === fieldId);
  const routesTo = field?.defaultAssigneeId
    ? members.find((m) => m.id === field.defaultAssigneeId)
    : undefined;

  // Putting work on a colleague is a grant of its own. Filing it outside every
  // field is not: unsorted work belongs to whoever has the global field view,
  // which is what the server checks too.
  const mayAssign = can(access, 'task.assign');
  const mayLeaveUnsorted = isAdmin(access?.role ?? null);

  const assignableMembers = mayAssign ? members : members.filter((m) => m.id === user?.id);

  function submit(event: FormEvent) {
    event.preventDefault();
    const trimmed = title.trim();
    if (!trimmed) return;

    void createTask({
      title: trimmed,
      description: description.trim(),
      fieldId: fieldId || null,
      priority,
      // Undefined, not null: the server then applies the field's own default.
      ...(assignee === AUTO ? {} : { assigneeId: assignee || null }),
    });
    dialogRef.current?.close();
  }

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      onClick={(event) => {
        if (event.target === dialogRef.current) dialogRef.current?.close();
      }}
      className="m-auto w-[min(34rem,calc(100vw-2rem))] rounded-xl border border-line bg-surface p-0 text-ink backdrop:bg-black/60 backdrop:backdrop-blur-sm"
    >
      <form onSubmit={submit} className="flex flex-col gap-5 p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold">New task</h2>
            <p className="mt-0.5 text-xs text-ink-faint">
              Give it a name and a body. Steps inside it can be added once it exists.
            </p>
          </div>
          <button
            type="button"
            onClick={() => dialogRef.current?.close()}
            className="btn btn-ghost !border-transparent !px-2"
            aria-label="Close"
          >
            <svg viewBox="0 0 16 16" className="size-4" fill="currentColor" aria-hidden="true">
              <path d="M4.28 3.22a.75.75 0 1 0-1.06 1.06L6.94 8l-3.72 3.72a.75.75 0 1 0 1.06 1.06L8 9.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L9.06 8l3.72-3.72a.75.75 0 0 0-1.06-1.06L8 6.94 4.28 3.22Z" />
            </svg>
          </button>
        </div>

        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-ink-soft">Title</span>
          <input
            ref={titleRef}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="What needs doing?"
            maxLength={300}
            required
            className="field-input text-sm"
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-ink-soft">Description</span>
          <textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={5}
            maxLength={10_000}
            placeholder="Context, decisions, links, how to tell it is finished."
            className="field-input resize-y text-sm leading-relaxed"
          />
        </label>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-ink-soft">Dev field</span>
            <select
              value={fieldId}
              onChange={(event) => setFieldId(event.target.value)}
              className="field-input text-sm"
            >
              {mayLeaveUnsorted && <option value="">Unsorted</option>}
              {fields.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-ink-soft">Assignee</span>
            <select
              value={assignee}
              onChange={(event) => setAssignee(event.target.value)}
              className="field-input text-sm"
            >
              <option value={AUTO}>By dev field</option>
              <option value="">Nobody</option>
              {assignableMembers.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.id === user?.id ? `${member.displayName} (me)` : member.displayName}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-ink-soft">Priority</span>
            <select
              value={priority}
              onChange={(event) => setPriority(event.target.value as Priority)}
              className="field-input text-sm"
            >
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {p[0]!.toUpperCase() + p.slice(1)}
                </option>
              ))}
            </select>
          </label>
        </div>

        {assignee === AUTO && (
          <p className="rounded-lg border border-line bg-canvas px-3 py-2 text-xs text-ink-faint">
            {routesTo ? (
              <>
                Lands with <span className="text-ink-soft">{routesTo.displayName}</span>, who{' '}
                {field?.name} routes its work to.
              </>
            ) : (
              <>
                {field
                  ? `${field.name} has nobody to route work to, so this starts unassigned.`
                  : 'Unsorted work starts unassigned.'}{' '}
                {mayLeaveUnsorted && 'Set a default in Settings to have it land automatically.'}
              </>
            )}
          </p>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-line pt-4">
          <button
            type="button"
            onClick={() => dialogRef.current?.close()}
            className="btn btn-ghost !py-1.5 !text-xs"
          >
            Cancel
          </button>
          <button type="submit" disabled={!title.trim()} className="btn btn-primary !py-1.5 !text-xs">
            Add task
          </button>
        </div>
      </form>
    </dialog>
  );
}
